import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Loader2 } from 'lucide-react';

const MINIMO = 10;

interface ErroInputJustificativaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numeroPedido: string;
  razaoSocial: string;
  cotacao: string;
  isLoading: boolean;
  onConfirm: (textoOperadora: string) => void;
}

export function ErroInputJustificativaDialog({
  open,
  onOpenChange,
  numeroPedido,
  razaoSocial,
  cotacao,
  isLoading,
  onConfirm,
}: ErroInputJustificativaDialogProps) {
  const [texto, setTexto] = useState('');

  useEffect(() => {
    if (!open) setTexto('');
  }, [open]);

  const tamanho = texto.trim().length;
  const podeConfirmar = tamanho >= MINIMO && !isLoading;

  return (
    <Dialog open={open} onOpenChange={(o) => !isLoading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[560px]" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            Erro de Input — Pedido {numeroPedido}
          </DialogTitle>
          <DialogDescription>
            {razaoSocial}
            {cotacao ? ` · Cotação ${cotacao}` : ''}
            <br />
            Cole o texto do erro que veio da operadora. Ele fica na timeline do pedido como
            ERRO DE INPUT, visível para o consultor e toda a equipe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 py-2">
          <Label htmlFor="erro-input-texto" className="text-xs font-semibold">
            Texto da operadora (obrigatório)
          </Label>
          <Textarea
            id="erro-input-texto"
            placeholder="Cole aqui a mensagem de erro exatamente como a operadora retornou..."
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={5}
            autoFocus
            disabled={isLoading}
            className="resize-none"
          />
          <p className={`text-[10px] ${tamanho < MINIMO ? 'text-destructive' : 'text-muted-foreground'}`}>
            {tamanho}/{MINIMO} caracteres mínimos
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button size="sm" onClick={() => onConfirm(texto.trim())} disabled={!podeConfirmar}>
            {isLoading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-1 h-4 w-4" />}
            Enviar para Erro de Input
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
